import React, { useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { buildAiBrief, BriefTarget } from 'shared/lib/widgetCode/aiBrief';
import type { HouseStyle } from 'shared/lib/widgetCode/houseStyle';
import { RECIPES } from 'shared/lib/widgetCode/recipes';
import type { SectionOption } from './usePageSections';
import useCopyText from './useCopyText';
import { T } from './tokens';

type Props = {
  /** Called with the pasted text when it should be checked. */
  onCheck: (raw: string) => void;
  /** The box was emptied: drop the proposal made from it. */
  onClear?: () => void;
  /** Changes when the box should empty itself (after a proposal is kept or discarded). */
  resetKey?: number;
  briefTarget: BriefTarget;
  houseStyle?: HouseStyle;
  /** Sections on the page the new one can be asked to match. */
  matchSections?: () => SectionOption[];
};

// Edits to a pasted reply are checked again once typing pauses.
const RECHECK_MS = 700;

const useStyles = createUseStyles({
  root: { display: 'grid', gap: 10, color: T.fg },
  area: {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 120,
    resize: 'vertical',
    padding: 12,
    fontFamily: T.mono,
    fontSize: 12,
    lineHeight: 1.5,
    border: `1px dashed ${T.fieldLine}`,
    borderRadius: 8,
    background: T.chrome,
    color: T.fg,
    '&:focus': { outline: `2px solid ${T.fg}`, outlineOffset: 1, background: T.field }
  },
  brief: { display: 'flex', gap: '6px 12px', flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: T.muted },
  link: {
    padding: 0,
    border: 0,
    background: 'none',
    font: 'inherit',
    fontWeight: 600,
    color: T.fg,
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    cursor: 'pointer'
  },
  quietLink: {
    padding: 0,
    border: 0,
    background: 'none',
    font: 'inherit',
    color: T.muted,
    cursor: 'pointer',
    '&:hover': { color: T.fg }
  },
  options: { display: 'flex', gap: '6px 12px', flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: T.muted },
  select: {
    padding: '6px 8px',
    fontSize: 12,
    border: `1px solid ${T.fieldLine}`,
    borderRadius: 6,
    background: T.field,
    color: T.fg,
    maxWidth: 220
  },
  hint: { fontSize: 12, color: T.muted },
  error: { fontSize: 12, color: T.red }
});

/**
 * Where an AI reply goes. Pasting checks it right away; edits are checked
 * again after a pause. Above it, one link copies instructions (the house
 * style, plus an optional layout and a section to match) for the AI tool.
 */
const PasteBox: React.FC<Props> = ({ onCheck, onClear, resetKey, briefTarget, houseStyle, matchSections }) => {
  const c = useStyles();
  const [text, setText] = useState('');
  const { copy, copied } = useCopyText();
  const [showOptions, setShowOptions] = useState(false);
  const [recipeId, setRecipeId] = useState('');
  const [matchId, setMatchId] = useState('');
  const [sections, setSections] = useState<SectionOption[]>([]);
  const [briefError, setBriefError] = useState<string | null>(null);
  // If the browser won't let us copy, show the brief so it can be copied by hand.
  const [briefFallback, setBriefFallback] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const onCheckRef = useRef(onCheck);
  onCheckRef.current = onCheck;

  // The page can change while the panel is open, so list its sections when the menu is used.
  const refreshSections = () => { if (matchSections) setSections(matchSections()); };
  useEffect(refreshSections, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelRecheck = () => { if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null; } };
  useEffect(() => cancelRecheck, []);

  // Kept or discarded: start over with an empty box.
  const firstReset = useRef(resetKey);
  useEffect(() => {
    if (resetKey === firstReset.current) return;
    cancelRecheck();
    setText('');
  }, [resetKey]);

  const copyBrief = async () => {
    setBriefError(null);
    let match = null;
    const chosen = sections.find(s => s.id === matchId);
    if (chosen) {
      try {
        match = await chosen.load();
      } catch (e) {
        setBriefError('Couldn’t load that section; the instructions were copied without it.');
      }
    }
    const brief = buildAiBrief(briefTarget, { houseStyle, recipeId: recipeId || null, match });
    const copiedOk = await copy('brief', brief);
    setBriefFallback(copiedOk ? null : brief);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // A reply pasted into the empty box is checked at once; pasting into text is an edit.
    if (text.trim()) return;
    const pasted = e.clipboardData.getData('text');
    if (pasted.trim()) {
      e.preventDefault();
      cancelRecheck();
      setText(pasted);
      onCheck(pasted);
    }
  };

  const handleChange = (value: string) => {
    setText(value);
    cancelRecheck();
    if (!value.trim()) {
      if (onClear) onClear();
      return;
    }
    timer.current = window.setTimeout(() => { timer.current = null; onCheckRef.current(value); }, RECHECK_MS);
  };

  return (
    <div className={c.root} data-testid="paste-box">
      <div className={c.brief} data-testid="brief-builder">
        <span>No code yet?</span>
        <button type="button" className={c.link} onClick={copyBrief}>
          {copied === 'brief' ? 'Copied: paste them into your AI tool' : 'Copy instructions for your AI tool'}
        </button>
        <button type="button" className={c.quietLink} aria-expanded={showOptions} onClick={() => setShowOptions(x => !x)}>
          {showOptions ? 'Hide options' : 'Options'}
        </button>
        {briefError && <span className={c.error}>{briefError}</span>}
      </div>
      {showOptions && (
        <div className={c.options} data-testid="brief-options">
          <label>
            Layout{' '}
            <select className={c.select} aria-label="Layout recipe" value={recipeId} onChange={e => setRecipeId(e.target.value)}>
              <option value="">Any</option>
              {RECIPES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </label>
          {matchSections && (
            <label>
              Match the look of{' '}
              <select className={c.select} aria-label="Match a section" value={matchId} onFocus={refreshSections} onMouseDown={refreshSections} onChange={e => setMatchId(e.target.value)}>
                <option value="">No section</option>
                {sections.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
      {briefFallback && (
        <div className={c.root} data-testid="brief-fallback">
          <span className={c.hint}>Your browser didn’t allow copying. Select the instructions below and copy them:</span>
          <textarea className={c.area} readOnly value={briefFallback} onFocus={e => e.target.select()} rows={8} />
        </div>
      )}
      <textarea
        className={c.area}
        aria-label="Paste the AI reply"
        placeholder="Paste the whole reply from your AI tool here."
        value={text}
        onChange={e => handleChange(e.target.value)}
        onPaste={handlePaste}
        spellCheck={false}
      />
    </div>
  );
};

export default PasteBox;
